import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import '../styles/SupportInbox.css';

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function errorText(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf';

function isImageAttachment(file) {
  return String(file?.content_type || '').startsWith('image/');
}

function MessageAttachments({ attachments }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  if (!attachments?.length) return null;
  return (
    <div className="support-inbox-attachments">
      {attachments.map((file) => (
        isImageAttachment(file) ? (
          <button key={file.id} type="button" onClick={() => setPreview(file.url)}>
            <img src={file.url} alt={file.filename || 'Attachment'} />
          </button>
        ) : (
          <a key={file.id} href={file.url} target="_blank" rel="noreferrer">
            {file.filename || 'Attachment'}
          </a>
        )
      ))}
      {preview ? (
        <div className="support-inbox-lightbox" onClick={() => setPreview(null)} role="presentation">
          <img src={preview} alt="" onClick={(event) => event.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
}

function SupportInbox() {
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  const refresh = useCallback(async () => {
    const data = await apiService.getMySupportThread();
    setThread(data);
    if (document.hidden || !(data?.unread_count > 0)) return data;
    await apiService.markMySupportThreadRead();
    setThread((current) => (current ? { ...current, unread_count: 0 } : current));
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    refresh()
      .catch((err) => {
        if (!cancelled) setError(errorText(err, 'Could not load messages'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    const timer = setInterval(() => {
      refresh().catch(() => {});
    }, 12000);
    const onVisible = () => {
      if (!document.hidden) refresh().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [thread?.messages?.length]);

  const addFiles = (event) => {
    const incoming = Array.from(event.target.files || []);
    event.target.value = '';
    if (!incoming.length) return;
    const oversized = incoming.find((file) => file.size > MAX_ATTACHMENT_BYTES);
    if (oversized) {
      setError(`${oversized.name} is larger than 10 MB`);
      return;
    }
    const next = [...files, ...incoming];
    if (next.length > MAX_ATTACHMENTS) {
      setError('You can attach up to 4 files');
      setFiles(next.slice(0, MAX_ATTACHMENTS));
      return;
    }
    setError('');
    setFiles(next);
  };

  const send = async () => {
    const text = draft.trim();
    if ((!text && files.length === 0) || !thread?.exists || thread.closed || sending) return;
    setSending(true);
    setError('');
    try {
      const data = await apiService.replyToSupportThread(text, files);
      setThread(data);
      setDraft('');
      setFiles([]);
    } catch (err) {
      setError(errorText(err, 'Could not send your reply'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <p className="support-inbox-note">Loading messages...</p>;
  }

  const hasMessages = (thread?.messages || []).length > 0;

  return (
    <section className="support-inbox">
      <header className="support-inbox-head">
        <h2>Messages</h2>
        <p>Replies to HomeCrowd support stay on this page.</p>
      </header>
      {error ? <p className="support-inbox-error">{error}</p> : null}
      <div className="support-inbox-log">
        {!hasMessages ? (
          <p className="support-inbox-note">
            When HomeCrowd writes to you about a receipt or your account, the conversation shows up here.
          </p>
        ) : null}
        {(thread?.messages || []).map((message) => (
          <article key={message.id} className={message.from_admin ? 'is-support' : 'is-mine'}>
            <header>
              <strong>{message.from_admin ? 'HomeCrowd' : 'You'}</strong>
              <time>{formatWhen(message.created_at)}</time>
            </header>
            {message.body ? <p>{message.body}</p> : null}
            <MessageAttachments attachments={message.attachments} />
          </article>
        ))}
        {thread?.closed && hasMessages ? <p className="support-inbox-ended">Chat ended</p> : null}
        <div ref={bottomRef} />
      </div>
      {thread?.exists && !thread.closed ? (
        <form
          className="support-inbox-composer"
          onSubmit={(event) => {
            event.preventDefault();
            send();
          }}
        >
          {files.length > 0 ? (
            <div className="support-inbox-files">
              {files.map((file, index) => (
                <button
                  key={`${file.name}-${index}`}
                  type="button"
                  onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                >
                  {file.name}
                  <span aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          ) : null}
          <div className="support-inbox-composer-row">
            <textarea
              value={draft}
              maxLength={2000}
              placeholder="Write a reply"
              onChange={(event) => setDraft(event.target.value)}
            />
            <button type="button" className="is-attach" onClick={() => fileInputRef.current?.click()}>
              Attach
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              hidden
              onChange={addFiles}
            />
            <button type="submit" className="is-send" disabled={sending || (!draft.trim() && files.length === 0)}>
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

export default SupportInbox;
